// src/context/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (!currentUser) {
          console.log('[Auth] No currentUser');
          setUser(null);
          setLoading(false);
          return;
        }

        // 1) Claims
        const tokenResult = await currentUser.getIdTokenResult();
        let role = tokenResult.claims.role || null;
        let status = 'active';

        // 2) Firestore
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        if (snap.exists()) {
          const data = snap.data();
          role = (data.role || role || 'user');
          status = (data.status || 'active');
          // ✅ normalize
          role = typeof role === 'string' ? role.trim().toLowerCase() : 'user';
          status = typeof status === 'string' ? status.trim().toLowerCase() : 'active';

          const nextUser = {
            ...currentUser,
            role,
            status,
            companyName: data.companyName,
            contactPerson: data.contactPerson,
            phone: data.phone,
            createdAt: data.createdAt,
            approvedAt: data.approvedAt,
            approvedBy: data.approvedBy,
          };

          console.log('[Auth] Setting user from Firestore', {
            uid: currentUser.uid,
            email: currentUser.email,
            role: nextUser.role,
            status: nextUser.status,
          });

          setUser(nextUser);
        } else {
          // fallback to claims/defaults
          role = typeof role === 'string' ? role.trim().toLowerCase() : 'user';
          status = 'active';

          const nextUser = { ...currentUser, role, status };

          console.log('[Auth] Setting user from claims/defaults', {
            uid: currentUser.uid,
            email: currentUser.email,
            role: nextUser.role,
            status: nextUser.status,
          });

          setUser(nextUser);
        }
      } catch (err) {
        console.error('[Auth] Error fetching user data', err);
        try {
          const tokenResult = await auth.currentUser?.getIdTokenResult();
          const role = tokenResult?.claims?.role ? tokenResult.claims.role.toString().trim().toLowerCase() : 'user';
          const nextUser = { ...auth.currentUser, role, status: 'active' };
          console.log('[Auth] Fallback user set after error', {
            uid: nextUser.uid, email: nextUser.email, role: nextUser.role, status: nextUser.status
          });
          setUser(nextUser);
        } catch (e) {
          console.error('[Auth] Secondary fallback failed', e);
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error.message);
    }
  };

  const refreshUser = async () => {
    if (auth.currentUser) {
      const snap = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (snap.exists()) {
        const data = snap.data();
        const role = (data.role || 'user').toString().trim().toLowerCase();
        const status = (data.status || 'active').toString().trim().toLowerCase();
        setUser(prev => ({ ...prev, ...data, role, status }));
        console.log('[Auth] refreshUser()', { role, status });
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
